import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

const STATUS_COLORS = {
  pending:    "bg-yellow-100 text-yellow-700",
  confirmed:  "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  shipped:    "bg-indigo-100 text-indigo-700",
  delivered:  "bg-green-100 text-green-700",
  cancelled:  "bg-red-100 text-red-700",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

// ── Customer Drawer ───────────────────────────────────────────
function CustomerDrawer({ customerId, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/admin/customers/${customerId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [customerId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Customer Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <p className="text-gray-400 text-sm animate-pulse">Loading...</p>
          </div>
        ) : !data ? (
          <div className="p-6 text-red-500 text-sm">Failed to load customer.</div>
        ) : (
          <div className="p-6 space-y-6">

            {/* Profile */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-2xl font-bold">
                {data.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{data.name}</p>
                <p className="text-sm text-gray-500">{data.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Joined {new Date(data.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{data.total_orders}</p>
                <p className="text-xs text-blue-500 mt-1">Total Orders</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-600">${Number(data.total_spent).toLocaleString()}</p>
                <p className="text-xs text-green-500 mt-1">Total Spent</p>
              </div>
            </div>

            {/* Addresses */}
            {data.addresses?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Saved Addresses</p>
                <div className="space-y-3">
                  {data.addresses.map(a => (
                    <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-0.5">
                          <p className="text-sm font-semibold text-gray-900">{a.full_name}</p>
                          <p className="text-sm text-gray-500">{a.phone}</p>
                          <p className="text-sm text-gray-500">{a.address_line1}</p>
                          {a.address_line2 && <p className="text-sm text-gray-500">{a.address_line2}</p>}
                          <p className="text-sm text-gray-500">{a.city}, {a.state} {a.zip}</p>
                          <p className="text-sm text-gray-500">{a.country}</p>
                        </div>
                        {a.is_default === 1 && (
                          <span className="text-xs bg-rose-100 text-rose-600 font-semibold px-2 py-1 rounded-full">Default</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Order History */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Order History</p>
              {data.orders?.length === 0 ? (
                <p className="text-sm text-gray-400">No orders yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.orders.map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Order #{o.id}</p>
                        <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">${Number(o.total).toLocaleString()}</p>
                        <StatusBadge status={o.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Customers Page ───────────────────────────────────────
export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [search,    setSearch]    = useState("");
  const [selected,  setSelected]  = useState(null);

  useEffect(() => { fetchCustomers(); }, []);

  async function fetchCustomers() {
    try {
      const res  = await fetch(`${API}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setCustomers(data);
    } catch {
      setError("Failed to load customers.");
    } finally {
      setLoading(false);
    }
  }

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading customers...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 text-sm mt-1">{customers.length} total customers</p>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Search */}
      <div className="mb-5">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Orders</th>
                <th className="px-5 py-3 text-left">Total Spent</th>
                <th className="px-5 py-3 text-left">Joined</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-sm font-bold">
                        {c.name?.charAt(0).toUpperCase()}
                      </div>
                      <p className="font-medium text-gray-900">{c.name}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-500">{c.email}</td>
                  <td className="px-5 py-3 text-gray-600">{c.total_orders}</td>
                  <td className="px-5 py-3 font-semibold text-gray-900">${Number(c.total_spent).toLocaleString()}</td>
                  <td className="px-5 py-3 text-gray-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => setSelected(c.id)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50 transition">
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No customers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Drawer */}
      {selected && (
        <CustomerDrawer
          customerId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}